import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RootState } from "../../app/store";
import { useAppSelector, useAppDispatch } from "../../app/hooks";
import {
  Grid as KendoGrid,
  GridColumn as Column,
  GridCellProps,
  GridFilterChangeEvent,
  GridPageChangeEvent,
  GridCustomHeaderCellProps,
  GridCustomCellProps,
} from "@progress/kendo-react-grid";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { Typography } from "@progress/kendo-react-common";
import { Button } from "@progress/kendo-react-buttons";
import {
  HeaderThElement,
  PagerTargetEvent,
} from "@progress/kendo-react-data-tools";
import { LoadingPanel } from "../../components/layout/Loading";
import {
  filterBy,
  CompositeFilterDescriptor,
} from "@progress/kendo-data-query";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import IconButton from "../../components/common/IconButton";
import { FiEdit } from "react-icons/fi";
import { MdDelete } from "react-icons/md";
import AppDialog from "../../components/dialog/Dialog";
import { DialogCloseButton } from "../../components/dialog/Dialog";
import { Switch } from "@progress/kendo-react-inputs";
import AlertBox from "../../components/common/AlertBox";
import { Tooltip } from "@progress/kendo-react-tooltip";
import {
  getAllTravelActivity,
  deleteTravelActivity,
  activeInactiveTravelActivity,
} from "./services/travelActivity.services";
import { clearTravelActivityID, setTravelActivityID } from "./travelActivitySlice";
import { checkAcessRights } from "../../_helper/helper";
import {
  DELETE_ACCESS,
  EDIT_ACCESS,
  INSERT_ACCESS,
} from "../../_contstants/common";

const TravelActivity: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isAddAccess = checkAcessRights(location.pathname, INSERT_ACCESS);

  useEffect(() => {
    dispatch(getAllTravelActivity({ Name: "" }));
    dispatch(closeDialog());
  }, []);

  const handleCreate = () => {
    navigate("/travelactivity/create");
  };

  return (
    <>
      <DeleteTravelActivityDialog />
      <GridLayout cols={[{ width: "100%" }]}>
        <GridLayoutItem
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 0px 20px",
          }}
        >
          <Typography.h4 style={{ marginBottom: 0, marginLeft: "3px" }}>
            Travel Activity Master
          </Typography.h4>
          <div>
            {isAddAccess && (
              <Button
                onClick={handleCreate}
                fillMode={"solid"}
                themeColor={"primary"}
              >
                Add New Activity
              </Button>
            )}
          </div>
        </GridLayoutItem>
        <GridLayoutItem>
          <TravelActivityGridComponent />
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

interface PageState {
  skip: number;
  take: number;
}

const initialDataState: PageState = { skip: 0, take: 10 };

const TravelActivityGridComponent: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isEditAccess = checkAcessRights(location.pathname, EDIT_ACCESS);
  const isDeleteAccess = checkAcessRights(location.pathname, DELETE_ACCESS);

  const list = useAppSelector((state: RootState) => state.travelActivity.TravelActivityList);
  const loading = useAppSelector((state: RootState) => state.travelActivity.loading);

  const [filter, setFilter] = React.useState<CompositeFilterDescriptor | undefined>(undefined);
  const [page, setPage] = React.useState<PageState>(initialDataState);
  const [pageSizeValue, setPageSizeValue] = React.useState<number | string | undefined>();

  const pageChange = (event: GridPageChangeEvent) => {
    const targetEvent = event.targetEvent as PagerTargetEvent;
    const take = targetEvent.value === "All" ? (list?.length || 0) : event.page.take;

    if (targetEvent.value) {
      setPageSizeValue(targetEvent.value);
    }
    setPage({
      ...event.page,
      take,
    });
  };

  const MyEditCommandCell = (props: GridCellProps) => {
    const handleEdit = (ID: number) => {
      navigate("/travelactivity/edit", { state: { TravelActivityID: ID } });
    };
    const handleOpenDeleteDialog = (ID: number) => {
      dispatch(openDialog("deleteTravelActivity"));
      dispatch(setTravelActivityID(ID));
    };
    return (
      <td className={`action-td ${props.className}`} style={{ ...props.style, right: 0 }}>
        {isEditAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Edit"
            onClick={() => handleEdit(props.dataItem.ID)}
            icon={FiEdit}
            iconClassName="absolute-position"
            iconStyle={{ fontSize: "20px" }}
          />
        )}
        {isDeleteAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Delete"
            className="color-red"
            onClick={() => handleOpenDeleteDialog(props.dataItem.ID)}
            icon={MdDelete}
            iconClassName="absolute-position"
            iconStyle={{ fontSize: "24px" }}
          />
        )}
      </td>
    );
  };

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
    setPage({ skip: 0, take: page.take });
  };

  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className={`table-header ${props.index === 3 || props.index === 4 ? "locked-header" : ""}`}
    >
      {props.children}
    </HeaderThElement>
  );

  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <td {...props.tdProps} colSpan={1} className={"table-cell"}>
      {props.children}
    </td>
  );

  const MyActiveInactiveCommandCell = (props: GridCellProps) => {
    const [isActive, setIsActive] = useState<boolean>(props.dataItem.IsActive);

    const handleActiveInactive = () => {
      const newValue = !isActive;
      setIsActive(newValue);
      dispatch(activeInactiveTravelActivity({ ID: props.dataItem.ID, IsActive: newValue })).then(() => {
        dispatch(getAllTravelActivity({ Name: "" }));
      });
    };

    return (
      <td style={{ textAlign: "center", ...props.style, right: 110 }} className={props.className}>
        <Tooltip anchorElement="target" position="top" parentTitle={true}>
          <div title={`Set ${isActive ? "Inactive" : "Active"}.`}>
            <Switch
              disabled={!isEditAccess}
              checked={isActive}
              onChange={handleActiveInactive}
              size="small"
            />
          </div>
        </Tooltip>
      </td>
    );
  };

  const gridRef = useRef<any>(null);

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      {list && list.length ? (
        <KendoGrid
          style={{ maxHeight: "calc(100vh - 152px)" }}
          className="responsive-table"
          filterable={true}
          filter={filter}
          data={filter ? filterBy(list, filter).slice(page.skip, page.take + page.skip) : list.slice(page.skip, page.take + page.skip)}
          onFilterChange={handleFilterChange}
          skip={page.skip}
          take={page.take}
          total={list.length}
          pageable={{
            buttonCount: 5,
            pageSizes: [5, 10, 15, "All"],
            pageSizeValue: pageSizeValue,
            type: "input",
          }}
          onPageChange={pageChange}
          cells={{
            headerCell: HeaderCustomCell,
            data: MyDataCustomCell,
          }}
        >
          <Column
            field="Name"
            title="Activity Name"
            width={300}
            cells={{
              data: (props: any) => (
                <td>
                  <div
                    className={isEditAccess ? "hoverableName" : "normalName"}
                    onClick={() =>
                      isEditAccess &&
                      navigate("/travelactivity/edit", {
                        state: { TravelActivityID: props.dataItem?.ID },
                      })
                    }
                  >
                    {props.dataItem?.Name}
                  </div>
                </td>
              ),
            }}
          />
          <Column
            field="TouristType"
            title="Tourist Type"
            width={150}
          />
          <Column
            field="ID"
            title="Status"
            width={"90px"}
            filterable={false}
            locked={true}
            cells={{
              data: MyActiveInactiveCommandCell,
            }}
          />
          {(isEditAccess || isDeleteAccess) && (
            <Column
              field="ID"
              title="Actions"
              width={"110px"}
              filterable={false}
              locked={true}
              cells={{
                data: MyEditCommandCell,
              }}
            />
          )}
        </KendoGrid>
      ) : (
        <AlertBox />
      )}
    </>
  );
};

const DeleteTravelActivityDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const dialogName = useAppSelector((state: RootState) => state.dialog.dialogName);
  const id = useAppSelector((state: RootState) => state.travelActivity.TravelActivityID);

  const handleDelete = async () => {
    if (id) {
      await dispatch(deleteTravelActivity(id));
      dispatch(getAllTravelActivity({ Name: "" }));
      dispatch(closeDialog());
      dispatch(clearTravelActivityID());
    }
  };

  return (
    <>
      {dialogName === "deleteTravelActivity" && (
        <AppDialog>
          <>
            <Typography.h5>{"Delete Travel Activity?"}</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>Are you sure you want to delete this record?</Typography.p>
              </GridLayoutItem>
              <GridLayoutItem style={{ display: "flex", justifyContent: "end" }}>
                <DialogCloseButton themeColor="error" />
                <Button fillMode={"solid"} themeColor={"error"} onClick={handleDelete} type="button">
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
    </>
  );
};

export default TravelActivity;
